/**
 * RemoveMemberDialog — confirmación para quitar un miembro del proyecto.
 *
 * A diferencia de DeleteProjectDialog, NO requiere type-to-confirm
 * porque la acción es reversible (basta volver a añadir al miembro).
 *
 * OT-0008 §4.4 — dos pasos, y el segundo solo aparece cuando hace falta.
 * El primer intento es un DELETE simple. Si el miembro es dueño de
 * artefactos con borrador abierto, el backend responde 409
 * `MEMBER_HAS_OPEN_DRAFTS` sin quitar a nadie; el diálogo entonces pide
 * un nuevo dueño y reintenta con `reassign_owned_to`. Quién bloquea lo
 * decide el backend, no el cliente: no se precalcula en el front para
 * no duplicar la regla en dos lugares.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../../api/client'
import {
  removeProjectMember,
  type ProjectMember,
} from '../../../api/project_members'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { canOwnArtifact } from '../../../lib/tiers'
import { pushToast } from '../../../stores/toastStore'

interface RemoveMemberDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  member: ProjectMember | null
  /** Miembros del proyecto — de aquí salen los candidatos a nuevo dueño. */
  members: ProjectMember[]
}

export function RemoveMemberDialog({
  open,
  onClose,
  projectId,
  member,
  members,
}: RemoveMemberDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  // Cuántos artefactos con borrador abierto bloquean la baja. null = aún
  // no lo sabemos (no hemos intentado, o el intento no chocó con esto).
  // El call site monta este diálogo con `key={member.id}`, así que el
  // estado del intento anterior muere con el componente — no hace falta
  // un efecto que lo resetee.
  const [blockedCount, setBlockedCount] = useState<number | null>(null)
  const [reassignTo, setReassignTo] = useState('')

  // Solo admin/editor del equipo operativo pueden ser dueños (D12,
  // OT-0058 §4.5), y no tiene sentido reasignarle al que estamos sacando.
  const ownerCandidates = members.filter(
    (m) =>
      m.id !== member?.id &&
      m.user_id !== null &&
      m.section === 'operational' &&
      canOwnArtifact(m.user_tier),
  )

  const displayName =
    member?.user_display_name ??
    member?.user_email ??
    member?.external_email ??
    '—'

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken || !member) throw new Error('no_context')
      await removeProjectMember(
        accessToken,
        projectId,
        member.id,
        reassignTo || undefined,
      )
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('team.toasts.removed', { name: displayName }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'members'],
      })
      onClose()
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      if (code === 'MEMBER_HAS_OPEN_DRAFTS') {
        const count =
          err instanceof ApiError
            ? Number(err.body?.details?.count ?? 0)
            : 0
        setBlockedCount(count)
        return
      }
      const fallback = t('team.toasts.remove_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  if (!member) return null

  const submitting = mutation.isPending
  const needsReassign = blockedCount !== null
  // Con la reasignación pendiente, el botón exige elegir dueño.
  const confirmDisabled = submitting || (needsReassign && reassignTo === '')

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submitting) return
        onClose()
      }}
      title={t('team.remove_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('team.remove_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={confirmDisabled}
            className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50"
          >
            {submitting
              ? t('team.remove_dialog.removing')
              : t('team.remove_dialog.confirm')}
          </button>
        </>
      }
    >
      <p className="font-body text-sm text-ink">
        {t('team.remove_dialog.lead', { name: displayName })}
      </p>
      {needsReassign && (
        <div className="mt-4 space-y-2">
          <p className="font-body text-sm text-ink">
            {t('team.remove_dialog.open_drafts', {
              name: displayName,
              count: blockedCount ?? 0,
            })}
          </p>
          {ownerCandidates.length === 0 ? (
            <p className="font-body text-sm text-critical">
              {t('team.remove_dialog.no_owner_candidates')}
            </p>
          ) : (
            <label className="block font-body text-sm text-ink-muted">
              {t('team.remove_dialog.reassign_label')}
              <select
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
                disabled={submitting}
                className="mt-1 w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink disabled:opacity-50"
              >
                <option value="">
                  {t('team.remove_dialog.reassign_placeholder')}
                </option>
                {ownerCandidates.map((c) => (
                  <option key={c.id} value={c.user_id ?? ''}>
                    {c.user_display_name ?? c.user_email ?? '—'}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </Dialog>
  )
}
